import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Instagram, ExternalLink, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lazy-loaded Instagram embeds card for property detail pages.
 *
 * - Accepts up to 3 IG URLs
 * - IntersectionObserver: only fetches when the card scrolls into view (-200px)
 * - Renders official Meta iframe HTML (or mock placeholder when in dev mode)
 * - One round-trip via /api/instagram/resolve?urls=a,b,c
 */
export default function InstagramEmbeds({ urls = [] }) {
  const { token } = useAuth();
  const { language } = useLanguage();
  const es = language === 'es';
  const cleanUrls = (urls || []).filter(Boolean).slice(0, 3);

  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState(null);
  const [liveMode, setLiveMode] = useState(true);
  const ref = useRef(null);

  // Reveal on scroll into view
  useEffect(() => {
    if (cleanUrls.length === 0) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setVisible(true); }),
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [cleanUrls.length]);

  // Fetch embed HTML lazily on first visibility
  useEffect(() => {
    if (!visible || items !== null || cleanUrls.length === 0) return;
    const q = encodeURIComponent(cleanUrls.join(','));
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/instagram/resolve?urls=${q}`, { headers })
      .then((r) => { setItems(r.data.items || []); setLiveMode(r.data.live_mode !== false); })
      .catch(() => setItems([]));
  }, [visible, items, cleanUrls, token]);

  // Load Instagram embed script once embeds are present (live mode only)
  useEffect(() => {
    if (!items || items.length === 0) return;
    if (!liveMode) return;
    if (window.instgrm?.Embeds) {
      window.instgrm.Embeds.process();
      return;
    }
    if (document.querySelector('script[data-ig-embed]')) return;
    const s = document.createElement('script');
    s.src = 'https://www.instagram.com/embed.js';
    s.async = true;
    s.dataset.igEmbed = '1';
    document.body.appendChild(s);
  }, [items, liveMode]);

  if (cleanUrls.length === 0) return null;

  return (
    <div
      ref={ref}
      className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#fffaf0] to-white p-5 sm:p-6 mt-6"
      data-testid="instagram-embeds-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] flex items-center justify-center">
          <Instagram className="w-4 h-4 text-white" />
        </div>
        <h3 className="font-heading text-base sm:text-lg text-[#0B192C]">
          {es ? 'Lo último de este agente en Instagram' : 'Latest from this agent on Instagram'}
        </h3>
      </div>

      {!visible && (
        <div className="text-xs text-slate-400" data-testid="ig-embeds-placeholder">
          {es ? 'Cargando al desplazarte…' : 'Loading when you scroll…'}
        </div>
      )}

      {visible && items === null && cleanUrls.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cleanUrls.map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {visible && items !== null && (
        <>
          {!liveMode && (
            <div className="flex items-start gap-2 mb-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ig-mock-banner">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{es
                ? 'Modo demo: el embed real de Instagram se mostrará cuando completemos la aprobación de Meta.'
                : 'Demo mode: real Instagram embeds will display once we complete Meta approval.'}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="ig-embeds-grid">
            {items.map((it, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden border border-slate-100" data-testid={`ig-embed-${i}`}>
                {it.error ? (
                  <a href={it.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-4 text-xs text-slate-500 hover:text-[#0B192C]">
                    <ExternalLink className="w-4 h-4" />
                    {es ? 'Ver en Instagram' : 'View on Instagram'}
                  </a>
                ) : (
                  <div
                    // Meta's HTML is from a trusted source — instagram_oembed returns sanitised markup
                    dangerouslySetInnerHTML={{ __html: it.html || '' }}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
